import { emailTestingAPI } from './email-testing.api'
import type {
  InboxComposeBody,
  InboxComposeResponse,
  InboxConfig,
  InboxListParams,
  InboxMessage,
  InboxStats,
  InboxWaitForBody
} from './email-testing.types'

/**
 * Pure async business logic for the inbox viewer. NO reactive state — the
 * store handles loading/error/filter state. Mirrors the `useXxxService`
 * convention used by every other ADX service module.
 */
export function useEmailTestingService() {
  const config = async (): Promise<InboxConfig> => {
    const response = await emailTestingAPI.config()
    return response.data
  }

  const stats = async (days?: number): Promise<InboxStats> => {
    const response = await emailTestingAPI.stats(days)
    return response.data
  }

  const list = async (params: InboxListParams = {}): Promise<InboxMessage[]> => {
    const response = await emailTestingAPI.list(params)
    return response.data.messages
  }

  /**
   * Paginated list. Returns `{ messages, nextCursor }`; pass back
   * `nextCursor` as `cursor` on the next call to walk pages. Null cursor
   * = terminal page.
   */
  const listPage = async (
    params: InboxListParams = {}
  ): Promise<{ messages: InboxMessage[]; nextCursor: string | null }> => {
    const response = await emailTestingAPI.list(params)
    return { messages: response.data.messages, nextCursor: response.data.nextCursor }
  }

  const get = async (id: string): Promise<InboxMessage> => {
    const response = await emailTestingAPI.get(id)
    return response.data.message
  }

  const clear = async (to: string): Promise<number> => {
    const response = await emailTestingAPI.clear(to)
    return response.data.deleted
  }

  const downloadAttachment = async (id: string, index: number): Promise<Blob> => {
    return emailTestingAPI.attachmentBlob(id, index)
  }

  const compose = async (body: InboxComposeBody): Promise<InboxComposeResponse> => {
    const response = await emailTestingAPI.compose(body)
    return response.data
  }

  const waitFor = async (body: InboxWaitForBody): Promise<InboxMessage | null> => {
    const response = await emailTestingAPI.waitFor(body)
    return response.data.message
  }

  return { config, stats, list, listPage, get, clear, downloadAttachment, compose, waitFor }
}
